import Big from 'big.js';
import * as Yup from 'yup';

import { Deal, DealStateEnum } from '../../gql/dealGql';
import {
  FinancialInfo,
  OptionTypeEnum,
  ProcessorEnum,
  isGapSelected,
} from '../../gql/financialInfoGql';
import { Bank, Maybe, TtNewBankEnum } from '../../gql/generated/graphql';

import { ALLY_MIN_MARKUP, LienholderNameEnum } from '../../constants/lienholders';
import { PermissionEnum } from '../../constants/permissions';
import {
  getTitleReceivedDateValidation,
  validateTodayOrEarlier,
} from '../../libs/yup-validators/dates';
import { MAX_TERM, ZERO_MARKUP_BANKS, findBankSlug } from '../../utils/financialInfos';

export const QUICK_NOTES_MAX_LENGTH = 100;
type ValidationContext = {
  deal?: Deal;
  abilities?: Set<PermissionEnum>;
  banks?: Bank[];
};

type DealValidationContext = {
  deal?: Deal;
  abilities?: Set<PermissionEnum>;
  bankSlug: Maybe<TtNewBankEnum> | undefined;
};

const getValidationContext = (
  options: Yup.TestContext<ValidationContext>['options'],
): DealValidationContext => ({
  deal: options.context?.deal,
  abilities: options.context?.abilities,
  bankSlug: findBankSlug({
    banks: options.context?.banks ?? [],
    bankName: options.context?.deal?.financial_info?.bank,
  }),
});

const testCantBeFutureValidation = Yup.date()
  .nullable()
  .test('testCantBeFuture', `Can't be in the future`, validateTodayOrEarlier);

// Apply the same validations to both fields just to be extra safe and keep the button validation logic as is.
// `newBuyRate` will show the error when editing, `buy_rate` will show the error when changed in the backend.
const buyRateValidation = Yup.number()
  .nullable()
  .required('Buy Rate is required')
  .moreThan(0, 'Buy Rate must be greater than 0')
  .test(
    'allyMinMarkup',
    `Markup can't be less than ${ALLY_MIN_MARKUP}%`,
    (buyRate, { parent, options }) => {
      const context = getValidationContext(options);
      const deal = context?.deal;
      const abilities = context?.abilities;
      const lienholder = deal?.car?.payoff?.lienholder_name;
      const { bankSlug } = context;
      const isAllyWithMarkupBank =
        lienholder === LienholderNameEnum.ALLY &&
        bankSlug != null &&
        !ZERO_MARKUP_BANKS.includes(bankSlug);

      if (!isAllyWithMarkupBank) {
        return true;
      }

      const originalBuyRate = deal?.financial_info?.buy_rate;
      const isBuyRateUnchanged = buyRate === originalBuyRate;
      const hasEditBuyRatePermission = abilities?.has(PermissionEnum.EditBuyRate);

      if (isBuyRateUnchanged || hasEditBuyRatePermission) {
        return true;
      }

      const { sell_rate } = parent as FinancialInfo;
      const currentMarkup = new Big(sell_rate ?? 0).minus(buyRate ?? 0).toNumber();

      return currentMarkup >= ALLY_MIN_MARKUP;
    },
  )
  .test('maxMarkup', (buyRate, { path, parent, createError }) => {
    const { sell_rate, maxMarkup } = parent;

    if (maxMarkup == null) {
      return true;
    }

    const currentMarkup = new Big(sell_rate ?? 0).minus(buyRate ?? 0).toNumber();
    if (currentMarkup <= maxMarkup) {
      return true;
    }

    return createError({
      message:
        maxMarkup === 0 ? 'Markup is not allowed' : `Markup can't be greater than ${maxMarkup}%`,
      path,
    });
  })
  .test('stateDocFeeMinMarkup', (buyRate, { path, parent, createError, options }) => {
    const context = getValidationContext(options);
    const deal = context?.deal;
    const abilities = context?.abilities;

    // If buy rate hasn't changed from the database value, allow it (admin may have set it)
    const originalBuyRate = deal?.financial_info?.buy_rate;
    if (buyRate === originalBuyRate) {
      return true;
    }

    // If user has EditBuyRate permission, skip minimum markup requirement
    if (abilities?.has(PermissionEnum.EditBuyRate)) {
      return true;
    }

    const { sell_rate, minMarkup } = parent;
    if (minMarkup == null) {
      return true;
    }

    const currentMarkup = new Big(sell_rate ?? 0).minus(buyRate ?? 0).toNumber();
    if (currentMarkup >= minMarkup) {
      return true;
    }

    return createError({
      message: `Markup can't be less than ${minMarkup}%`,
      path,
    });
  });

export const validationSchema = Yup.object().shape({
  car: Yup.object().shape({
    payoff: Yup.object().shape({
      vehicle_payoff: Yup.number()
        .moreThan(0, 'Payoff must be greater than 0')
        .required('Payoff is required')
        .nullable(),
    }),
  }),
  customer: Yup.object().shape({
    proof_of_insurance: Yup.object()
      .nullable()
      .shape({
        company_name: Yup.string()
          .nullable()
          .test((company_name, { options }) => {
            const ctx = getValidationContext(options);
            const state = ctx?.deal?.state;

            const companyIsRequired = state === DealStateEnum.Closing;
            if (companyIsRequired) {
              return !!company_name;
            }
            return true;
          }),
        policy_number: Yup.string()
          .nullable()
          .test((policy_number, { options }) => {
            const ctx = getValidationContext(options);
            const state = ctx?.deal?.state;

            const numberIsRequired = state === DealStateEnum.Closing;
            if (numberIsRequired) {
              return !!policy_number;
            }
            return true;
          }),
        expires: Yup.date()
          .nullable()
          .test((expires, { options }) => {
            const ctx = getValidationContext(options);
            const state = ctx?.deal?.state;

            const dateIsRequired = state === DealStateEnum.Closing;
            if (dateIsRequired) {
              return !!expires;
            }
            return true;
          }),
      }),
  }),
  financial_info: Yup.object().shape({
    money_down: Yup.number().required('Money Down is required').nullable(),
    bank_fees: Yup.number().required('Bank Fees is required').nullable(),
    base_tax_amount: Yup.number().optional().nullable(),
    warranty_tax_amount: Yup.number().optional().nullable(),
    title_fee: Yup.number()
      .moreThan(0, 'Title must be greater than 0')
      .required('Title is required')
      .nullable(),
    new_registration_fee: Yup.number().nullable().required('Registration is required'),
    sell_rate: Yup.number()
      .nullable()
      .required('Sell Rate is required')
      .moreThan(0, 'Sell Rate must be greater than 0')
      .test(
        'sellRateGreaterThanBuyRate',
        `Sell Rate can't be less than Buy Rate`,
        (sellRate, { parent, options }) => {
          if (getValidationContext(options).bankSlug === TtNewBankEnum.FifthThird) {
            return true;
          }

          const { buy_rate } = parent as FinancialInfo;

          return new Big(sellRate ?? 0).toNumber() >= new Big(buy_rate ?? 0).toNumber();
        },
      )
      .test(
        'allyMinMarkupSellRate',
        `Markup can't be less than ${ALLY_MIN_MARKUP}%`,
        (sellRate, { parent, options }) => {
          const context = getValidationContext(options);
          const deal = context?.deal;
          const abilities = context?.abilities;
          const lienholder = deal?.car?.payoff?.lienholder_name;
          const { bankSlug } = context;
          const isAllyWithMarkupBank =
            lienholder === LienholderNameEnum.ALLY &&
            bankSlug != null &&
            !ZERO_MARKUP_BANKS.includes(bankSlug);

          if (!isAllyWithMarkupBank) {
            return true;
          }

          const originalSellRate = deal?.financial_info?.sell_rate;
          const isSellRateUnchanged = sellRate === originalSellRate;
          const hasEditBuyRatePermission = abilities?.has(PermissionEnum.EditBuyRate);

          if (isSellRateUnchanged || hasEditBuyRatePermission) {
            return true;
          }

          const { buy_rate } = parent as FinancialInfo;
          const currentMarkup = new Big(sellRate ?? 0).minus(buy_rate ?? 0).toNumber();

          return currentMarkup >= ALLY_MIN_MARKUP;
        },
      )
      .test('maxMarkupSellRate', (sellRate, { path, parent, createError }) => {
        const { buy_rate, maxMarkup } = parent;

        if (maxMarkup == null) {
          return true;
        }

        const currentMarkup = new Big(sellRate ?? 0).minus(buy_rate ?? 0).toNumber();
        if (currentMarkup <= maxMarkup) {
          return true;
        }

        return createError({
          message:
            maxMarkup === 0
              ? 'Markup is not allowed'
              : `Markup can't be greater than ${maxMarkup}%`,
          path,
        });
      })
      .test('stateDocFeeMinMarkupSellRate', (sellRate, { path, parent, createError, options }) => {
        const context = getValidationContext(options);
        const deal = context?.deal;
        const abilities = context?.abilities;

        // If sell rate hasn't changed from the database value, allow it (admin may have set it)
        const originalSellRate = deal?.financial_info?.sell_rate;
        if (sellRate === originalSellRate) {
          return true;
        }

        // If user has EditBuyRate permission, skip minimum markup requirement
        if (abilities?.has(PermissionEnum.EditBuyRate)) {
          return true;
        }

        const { buy_rate, minMarkup } = parent;
        if (minMarkup == null) {
          return true;
        }

        const currentMarkup = new Big(sellRate ?? 0).minus(buy_rate ?? 0).toNumber();
        if (currentMarkup >= minMarkup) {
          return true;
        }

        return createError({
          message: `Markup can't be less than ${minMarkup}%`,
          path,
        });
      }),
    term: Yup.number()
      .moreThan(0, 'Term must be greater than 0')
      .integer('Term must be a whole number of months')
      .max(MAX_TERM, `Term can't be more than ${MAX_TERM} months`)
      .required('Term is required')
      .nullable(),
    vsc_price: Yup.number()
      .nullable()
      .required('VSC Price is required')
      .test(
        'lowerThanCost',
        'VSC Price can not be lower than VSC Cost',
        (vsc_price, { parent }) => {
          const { vsc_cost } = parent as { vsc_cost?: number };

          return (vsc_price ?? 0) >= (vsc_cost ?? 0);
        },
      ),
    vsc_cost: Yup.number()
      .nullable()
      .required('VSC Cost is required')
      .test(
        'costHigherThanPrice',
        'VSC Cost can not be higher than VSC Price',
        (vsc_cost, { parent }) => {
          const { vsc_price } = parent as { vsc_price?: number };

          return (vsc_price ?? 0) >= (vsc_cost ?? 0);
        },
      ),
    gap_price: Yup.number().when('option_type', {
      is: (option_type?: OptionTypeEnum) => isGapSelected(option_type),
      then: Yup.number()
        .nullable()
        .required('GAP Price is required')
        .test(
          'lowerThanCost',
          'GAP Price can not be lower than GAP Cost',
          (gap_price, { parent }) => {
            const { gap_cost } = parent as { gap_cost?: number };

            return (gap_price ?? 0) >= (gap_cost ?? 0);
          },
        ),
      otherwise: Yup.number().nullable(),
    }),
    gap_cost: Yup.number().when('option_type', {
      is: (option_type?: OptionTypeEnum) => isGapSelected(option_type),
      then: Yup.number()
        .nullable()
        .required('GAP Cost is required')
        .test(
          'costHigherThanPrice',
          'GAP Cost can not be higher than GAP Price',
          (gap_cost, { parent }) => {
            const { gap_price } = parent as { gap_price?: number };

            return (gap_price ?? 0) >= (gap_cost ?? 0);
          },
        ),
      otherwise: Yup.number()
        .nullable()
        .test(
          'costHigherThanPrice',
          'GAP Cost can not be higher than GAP Price',
          (gap_cost, { parent }) => {
            if (gap_cost == null) {
              return true;
            }
            const { gap_price } = parent as { gap_price?: number };

            return (gap_price ?? 0) >= (gap_cost ?? 0);
          },
        ),
    }),
    buy_rate: buyRateValidation,
    processor: Yup.string()
      .nullable()
      .test('processor test', 'Processor is required', (value, { options }) => {
        const context = getValidationContext(options);
        if (context?.deal?.state === DealStateEnum.Finalized) {
          return !!value && Object.values(ProcessorEnum).includes(value as ProcessorEnum);
        }
        return true;
      }),
    tt_transaction_id: Yup.string().optional().nullable(),
    user_entered_reserve: Yup.number().optional().nullable(),
    quick_notes: Yup.string()
      .optional()
      .nullable()
      .max(QUICK_NOTES_MAX_LENGTH, `Please limit to ${QUICK_NOTES_MAX_LENGTH} characters`),

    // Switches section.
    plate_transfer: Yup.boolean().optional().nullable(),
    buyer_not_lessee: Yup.boolean().optional().nullable(),
    needs_temporary_registration_tags: Yup.boolean().optional().nullable(),

    // Frontend only fields.
    newBaseTaxAmount: Yup.number().optional().nullable(),
    totalTaxAmount: Yup.number().optional().nullable(),
    feesQueryFailed: Yup.boolean().optional().nullable(),
    tempUserEnteredReserve: Yup.number().optional().nullable(),
    newBuyRate: buyRateValidation,
  }),
  tire_wheel_price: Yup.number()
    .nullable()
    .optional()
    .test(
      'lowerThanCost',
      'T&W Price can not be lower than T&W Cost',
      (tire_wheel_price, { parent }) => {
        if (tire_wheel_price == null) {
          return true;
        }
        const { tire_wheel_cost } = parent as { tire_wheel_cost?: number };

        return (tire_wheel_price ?? 0) >= (tire_wheel_cost ?? 0);
      },
    ),
  tire_wheel_cost: Yup.number()
    .nullable()
    .optional()
    .test(
      'costHigherThanPrice',
      'T&W Cost can not be higher than T&W Price',
      (tire_wheel_cost, { parent }) => {
        if (tire_wheel_cost == null) {
          return true;
        }
        const { tire_wheel_price } = parent as { tire_wheel_price?: number };

        return (tire_wheel_price ?? 0) >= (tire_wheel_cost ?? 0);
      },
    ),
  key_replacement_price: Yup.number()
    .nullable()
    .optional()
    .test(
      'lowerThanCost',
      'Key Replacement Price can not be lower than Key Replacement Cost',
      (key_replacement_price, { parent }) => {
        if (key_replacement_price == null) {
          return true;
        }
        const { key_replacement_cost } = parent as { key_replacement_cost?: number };

        return (key_replacement_price ?? 0) >= (key_replacement_cost ?? 0);
      },
    ),
  key_replacement_cost: Yup.number()
    .nullable()
    .optional()
    .test(
      'costHigherThanPrice',
      'Key Replacement Cost can not be higher than Key Replacement Price',
      (key_replacement_cost, { parent }) => {
        if (key_replacement_cost == null) {
          return true;
        }
        const { key_replacement_price } = parent as { key_replacement_price?: number };

        return (key_replacement_price ?? 0) >= (key_replacement_cost ?? 0);
      },
    ),
  deal_dates: Yup.object().shape({
    custom_dates: Yup.object()
      .nullable(true)
      .shape({
        [DealStateEnum.Signed]: Yup.date()
          .required('Signed Date is required')
          .typeError('Invalid Date')
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .test('testCantBeFuture', `Can't be in the future`, validateTodayOrEarlier),
        [DealStateEnum.WaitingForTitle]: Yup.date().optional().nullable(),
        [DealStateEnum.TitleReceived]: getTitleReceivedDateValidation(),
        [DealStateEnum.SentToProcessor]: testCantBeFutureValidation,
        sent_to_audit: Yup.date().optional().nullable(),
        wet_sig_sent_date: testCantBeFutureValidation,
        wet_sig_received_date: testCantBeFutureValidation,
      }),
  }),
});
